import React, { useMemo } from 'react';
import './Coordinates.css';
import {
  buildMatrixLabels,
  buildSequentialLabels,
  buildEndzoneRowLabels,
} from './coordinateFormatting';

const Coordinates = ({
  orientation = 'cols',
  side,
  count = 0,
  cellSize = 40,
  padding = 40,
  viewPerspective = 'north',
  coordSystem = 'endzone',
  labelColor,
  positionOffset,
  reversePositions = false,
}) => {
  const isFlipped = viewPerspective === 2 || viewPerspective === 'south';

  const labels = useMemo(() => {
    if (coordSystem === 'matrix') {
      return buildMatrixLabels(count, isFlipped);
    }
    if (orientation === 'rows') {
      return buildEndzoneRowLabels(count, isFlipped);
    }
    return buildSequentialLabels(count, isFlipped);
  }, [coordSystem, orientation, count, isFlipped]);
  const entries = Array.isArray(labels) ? labels : [];
  const entryCount = entries.length;
  const usableCount = Math.max(entryCount, 0);
  const trackLength = Math.max(usableCount - 1, 0) * cellSize;
  const defaultOffset = Math.max(4, padding - cellSize * 0.5);

  // Determine effective side from orientation if not explicitly provided
  const effectiveSide = side || (orientation === 'rows' ? 'left' : 'top');

  // For mirror sides, reverse spatial positions so they read correctly after 180° rotation
  const pos = (idx) => reversePositions
    ? (entryCount - idx + 0.5) * cellSize
    : (idx + 1.5) * cellSize;

  if (orientation === 'rows') {
    const style = {
      left: `${positionOffset ?? defaultOffset}px`,
      top: `${padding}px`,
      height: `${trackLength || cellSize}px`,
      width: `${cellSize * 0.8}px`,
    };
    if (labelColor) {
      style.textShadow = `0 0 4px ${labelColor}, 0 0 10px ${labelColor}, 0 0 18px ${labelColor}`;
    }

    return (
      <div
        className={`coords coords--rows coords--${effectiveSide}`}
        style={style}
      >
        {entries.map((label, idx) => (
          <span key={`row-${idx}`} style={{ top: `${pos(idx)}px` }}>
            {label}
          </span>
        ))}
      </div>
    );
  }

  const style = {
    left: `${padding}px`,
    top: `${positionOffset ?? defaultOffset}px`,
    width: `${trackLength || cellSize}px`,
    height: `${cellSize * 0.8}px`,
  };
  if (labelColor) {
    style.textShadow = `0 0 4px ${labelColor}, 0 0 10px ${labelColor}, 0 0 18px ${labelColor}`;
  }

  return (
    <div
      className={`coords coords--cols coords--${effectiveSide}`}
      style={style}
    >
      {entries.map((label, idx) => (
        <span key={`col-${idx}`} style={{ left: `${pos(idx)}px` }}>
          {label}
        </span>
      ))}
    </div>
  );
};

export default Coordinates;
